import { Link } from "react-router-dom";
import { Store, HeartHandshake, Gift, LifeBuoy, ArrowRight, Check, Leaf } from "lucide-react";
import { PublicNav } from "@/components/PublicNav";

const HERO = "/hero-community.jpg";

const cards = [
  {
    id: "grocery",
    icon: Store,
    title: "I'm a Grocery Store",
    subtitle: "Stop paying to throw away edible food.",
    tone: "emerald",
    benefits: ["Unlimited food rescue", "Zero-effort pickups", "ESG reporting", "Tax documentation", "Staff time saved"],
    cta: "Start rescuing food",
    to: "/register?role=grocery_store",
    learn: "/for-grocery-stores",
  },
  {
    id: "org",
    icon: HeartHandshake,
    title: "I'm an Aid Organization",
    subtitle: "Receive food, supplies, and donations from your community.",
    tone: "emerald",
    benefits: ["Claim grocery listings", "Create supply wishlists", "Receive Amazon shipments", "Manage requests", "Coordinate pickups"],
    cta: "Join Gather",
    to: "/register?role=aid_organization",
    learn: "/for-aid-organizations",
  },
  {
    id: "donor",
    icon: Gift,
    title: "I Want to Help",
    subtitle: "Buy exactly what local organizations and people need.",
    tone: "orange",
    chips: ["Tent", "Sleeping bag", "Emergency blanket", "Socks", "Hygiene kit"],
    cta: "Help someone today",
    to: "/help",
  },
  {
    id: "need",
    icon: LifeBuoy,
    title: "I Need Help",
    subtitle: "Request supplies, or find shelters, showers, and pantries near you.",
    tone: "orange",
    benefits: ["Find shelters & warming centers", "Find showers & food pantries", "Find nearby aid organizations", "Request supplies for pickup"],
    cta: "Aid near me",
    to: "/near-me",
    cta2: "Request help",
    to2: "/near-me?request=1",
    note: "Always free. No login or email required.",
  },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-stone-50">
      <PublicNav />

      {/* Hero */}
      <section className="relative pt-16 min-h-[70vh] flex items-center">
        <img src={HERO} alt="Fresh produce" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full py-20">
          <div className="max-w-3xl animate-fade-up">
            <span className="inline-flex items-center gap-2 text-xs tracking-widest uppercase text-emerald-50 bg-emerald-800/60 backdrop-blur px-4 py-1.5 rounded-full">
              <Leaf className="w-3.5 h-3.5" /> One platform · four ways to help
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-white mt-6 leading-[1.05]">
              Gather. Give. Grow.
            </h1>
            <p className="font-heading text-xl sm:text-2xl text-emerald-50 mt-4">
              Community provisioning, reimagined.
            </p>
            <p className="text-lg text-stone-100/90 mt-5 leading-relaxed max-w-2xl">
              Connecting grocery stores, nonprofits, donors, and neighbors through one coordinated platform.
            </p>
          </div>
        </div>
      </section>

      {/* Four entry cards */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 -mt-16 relative z-10 pb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {cards.map((c) => (
            <div key={c.id} data-testid={`entry-card-${c.id}`} className="bg-white rounded-3xl border border-stone-200 shadow-sm hover:shadow-md transition-all p-8 flex flex-col">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${c.tone === "orange" ? "bg-orange-50 text-orange-700" : "bg-emerald-50 text-emerald-800"}`}>
                <c.icon className="w-6 h-6" />
              </div>
              <h2 className="font-heading text-2xl font-medium text-stone-900 mt-5">{c.title}</h2>
              <p className="text-stone-600 mt-1">{c.subtitle}</p>

              {c.benefits && (
                <ul className="mt-5 space-y-2 flex-1">
                  {c.benefits.map((b) => (
                    <li key={b} className="flex items-center gap-2 text-sm text-stone-700">
                      <Check className={`w-4 h-4 ${c.tone === "orange" ? "text-orange-600" : "text-emerald-700"}`} /> {b}
                    </li>
                  ))}
                </ul>
              )}
              {c.chips && (
                <div className="mt-5 flex flex-wrap gap-2 flex-1 content-start">
                  {c.chips.map((chip) => (
                    <span key={chip} className="text-xs font-medium bg-stone-100 text-stone-700 rounded-full px-3 py-1.5">{chip}</span>
                  ))}
                </div>
              )}

              <div className="mt-6 flex items-center gap-3 flex-wrap">
                <Link data-testid={`entry-cta-${c.id}`} to={c.to} className={`inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium text-white transition-all hover:-translate-y-0.5 ${c.tone === "orange" ? "bg-orange-700 hover:bg-orange-800" : "bg-emerald-800 hover:bg-emerald-900"}`}>
                  {c.cta} <ArrowRight className="w-4 h-4" />
                </Link>
                {c.to2 && (
                  <Link data-testid={`entry-cta2-${c.id}`} to={c.to2} className="inline-flex items-center gap-2 rounded-full px-6 py-3 font-medium border-2 border-orange-700 text-orange-800 hover:bg-orange-50 transition-all hover:-translate-y-0.5">
                    {c.cta2} <ArrowRight className="w-4 h-4" />
                  </Link>
                )}
                {c.learn && <Link to={c.learn} className="text-sm font-medium text-stone-600 hover:text-emerald-800 underline underline-offset-4">Learn more</Link>}
              </div>
              {c.note && <p className="text-xs text-stone-400 mt-3">{c.note}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* Ecosystem: aid orgs are the bridge */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="text-center max-w-2xl mx-auto">
          <span className="text-xs tracking-widest uppercase text-orange-700 font-medium">How the ecosystem works</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-stone-900 mt-3">
            Aid organizations are the heart of Gather.
          </h2>
          <p className="text-stone-600 mt-4 leading-relaxed">
            They're the bridge that connects everyone — receiving surplus from grocery stores, fulfilling supply needs
            funded by donors, and approving requests from people who need help.
          </p>
        </div>
        <div className="mt-12 flex flex-col md:flex-row items-stretch justify-center gap-4">
          {[
            { icon: Store, label: "Grocery Stores", sub: "Post surplus food" },
            { icon: HeartHandshake, label: "Aid Organizations", sub: "Receive · fulfill · distribute", bridge: true },
            { icon: Gift, label: "Donors", sub: "Fund needed items" },
            { icon: LifeBuoy, label: "People in Need", sub: "Request help" },
          ].map((n, i) => (
            <div key={n.label} className="flex items-center gap-4">
              <div className={`rounded-2xl border p-6 text-center w-48 ${n.bridge ? "bg-emerald-900 text-emerald-50 border-emerald-900 shadow-lg" : "bg-white border-stone-200"}`}>
                <n.icon className={`w-6 h-6 mx-auto ${n.bridge ? "text-emerald-100" : "text-emerald-800"}`} />
                <p className={`font-heading font-medium mt-3 ${n.bridge ? "text-white" : "text-stone-900"}`}>{n.label}</p>
                <p className={`text-xs mt-1 ${n.bridge ? "text-emerald-200/80" : "text-stone-500"}`}>{n.sub}</p>
              </div>
              {i < 3 && <ArrowRight className="w-5 h-5 text-stone-300 hidden md:block shrink-0" />}
            </div>
          ))}
        </div>
      </section>

      {/* Charity model */}
      <section className="bg-emerald-900 text-emerald-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20 grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-8">
            <span className="text-xs tracking-widest uppercase text-emerald-300/80 font-medium flex items-center gap-2"><HeartHandshake className="w-4 h-4" /> Where the money goes</span>
            <h2 className="font-heading text-3xl sm:text-4xl font-medium tracking-tight text-white mt-4">
              Only grocery stores pay — and 100% of proceeds are donated.
            </h2>
            <p className="text-emerald-100/85 mt-4 leading-relaxed max-w-2xl">
              Gather is a paid subscription for grocery stores only. Every dollar of that revenue — and every donation
              made through the platform — goes to <span className="font-medium text-white">Partnership to End Addiction</span>.
              It's always free, with no login or email, for aid organizations and people in need.
            </p>
          </div>
          <div className="lg:col-span-4">
            <div className="rounded-2xl bg-emerald-800/60 border border-emerald-700 p-8 text-center">
              <p className="text-xs tracking-widest uppercase text-emerald-300/80">Proceeds benefit</p>
              <p className="font-heading text-2xl font-semibold mt-2 leading-tight text-white">Partnership to End Addiction</p>
              <p className="font-heading text-5xl font-semibold mt-4 text-white">100%</p>
              <p className="text-emerald-200/80 text-sm mt-1">of all revenue donated</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-stone-200 bg-stone-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Logo size={24} />
          <p className="text-sm text-stone-500">Rescue surplus. Meet real needs. Strengthen local communities.</p>
          <div className="flex items-center gap-4 text-sm text-stone-500">
            <Link to="/transparency" className="hover:text-emerald-800">Transparency</Link>
            <Link to="/terms" className="hover:text-emerald-800">Terms</Link>
            <Link to="/privacy" className="hover:text-emerald-800">Privacy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Logo({ size = 24 }) {
  return (
    <span className="flex items-center gap-2">
      <img src="/logo.png" alt="Gather" width={size} height={size} className="object-contain" />
      <span className="font-brand font-extrabold text-lg text-stone-900">Gather</span>
    </span>
  );
}
